<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const isSaving = ref(false);
const quality = ref('');
const transferReason = ref('');
const notes = ref('');

const qualityOptions = [
  { value: '', label: 'Sem avaliação' },
  { value: 'good', label: 'Boa' },
  { value: 'review', label: 'Revisar' },
  { value: 'critical', label: 'Crítica' },
];

const transferReasonOptions = [
  { value: '', label: 'Sem transferência' },
  { value: 'complex_request', label: 'Solicitação complexa' },
  { value: 'commercial_intent', label: 'Intenção comercial' },
  { value: 'customer_requested_human', label: 'Cliente pediu humano' },
  { value: 'low_confidence', label: 'Baixa confiança da IA' },
  { value: 'policy_or_sensitive', label: 'Caso sensível' },
];

const copy = {
  title: 'Supervisão IA',
  description: 'Qualidade, motivo de transferência e histórico desta conversa.',
  active: 'IA ativa',
  paused: 'IA pausada',
  quality: 'Qualidade da IA',
  transferReason: 'Motivo de transferência para humano',
  notes: 'Nota da supervisão',
  timeline: 'Timeline da IA',
};

const customAttributes = computed(
  () => props.conversation?.custom_attributes || {}
);

const isAiActive = computed(
  () =>
    props.conversation?.meta?.assignee_type === 'AgentBot' ||
    Boolean(props.conversation?.assignee_agent_bot_id)
);

const hasAiContext = computed(
  () =>
    isAiActive.value ||
    Boolean(customAttributes.value.ai_quality) ||
    Boolean(customAttributes.value.ai_transfer_reason) ||
    Boolean(customAttributes.value.ai_supervision_notes)
);

const qualityLabel = computed(
  () =>
    qualityOptions.find(option => option.value === quality.value)?.label ||
    'Sem avaliação'
);

const transferReasonLabel = computed(
  () =>
    transferReasonOptions.find(option => option.value === transferReason.value)
      ?.label || 'Sem transferência'
);

const timeline = computed(() => {
  const savedTimeline = Array.isArray(customAttributes.value.ai_timeline)
    ? customAttributes.value.ai_timeline
    : [];

  return [
    ...(isAiActive.value
      ? [
          {
            event: 'IA em atendimento',
            at:
              props.conversation?.updated_at || props.conversation?.created_at,
          },
        ]
      : []),
    ...savedTimeline,
  ].slice(-5);
});

const formatTimelineDate = value => {
  if (!value) return 'agora';
  const timestamp = String(value).length === 10 ? Number(value) * 1000 : value;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
};

const syncFields = () => {
  quality.value = customAttributes.value.ai_quality || '';
  transferReason.value = customAttributes.value.ai_transfer_reason || '';
  notes.value = customAttributes.value.ai_supervision_notes || '';
};

watch(
  () => [props.conversation?.id, props.conversation?.custom_attributes],
  syncFields,
  { immediate: true }
);

const saveSupervision = async () => {
  if (!props.conversation?.id) return;
  isSaving.value = true;

  const existingTimeline = Array.isArray(customAttributes.value.ai_timeline)
    ? customAttributes.value.ai_timeline
    : [];
  const nextTimeline = [
    ...existingTimeline,
    {
      event: `Supervisão atualizada: ${qualityLabel.value}`,
      detail: transferReason.value ? transferReasonLabel.value : null,
      at: new Date().toISOString(),
    },
  ].slice(-10);

  await store.dispatch('updateCustomAttributes', {
    conversationId: props.conversation.id,
    customAttributes: {
      ...customAttributes.value,
      ai_quality: quality.value,
      ai_transfer_reason: transferReason.value,
      ai_supervision_notes: notes.value,
      ai_timeline: nextTimeline,
    },
  });

  isSaving.value = false;
  useAlert('Supervisão da IA salva.');
};
</script>

<template>
  <div>
    <section
      v-if="hasAiContext"
      class="mx-2 mb-3 rounded-lg border border-n-teal-5 bg-n-teal-2/60 p-4"
    >
      <div class="mb-4 flex items-start justify-between gap-3">
        <div>
          <div
            class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-bot size-4 text-n-teal-10" />
            {{ copy.title }}
          </div>
          <p class="m-0 mt-1 text-xs text-n-slate-11">
            {{ copy.description }}
          </p>
        </div>
        <span
          class="inline-flex flex-shrink-0 items-center rounded-md bg-n-teal-4 px-2 py-1 text-xs font-medium text-n-teal-11"
        >
          {{ isAiActive ? copy.active : copy.paused }}
        </span>
      </div>

      <div class="grid gap-3">
        <label class="grid gap-1 text-xs font-medium text-n-slate-11">
          {{ copy.quality }}
          <select
            v-model="quality"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option
              v-for="option in qualityOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="grid gap-1 text-xs font-medium text-n-slate-11">
          {{ copy.transferReason }}
          <select
            v-model="transferReason"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option
              v-for="option in transferReasonOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="grid gap-1 text-xs font-medium text-n-slate-11">
          {{ copy.notes }}
          <textarea
            v-model="notes"
            rows="3"
            class="resize-none rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            placeholder="Resumo curto do que a IA fez bem ou precisa revisar."
          />
        </label>
      </div>

      <div class="mt-4 border-t border-n-teal-5 pt-3">
        <div class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
          {{ copy.timeline }}
        </div>
        <div class="grid gap-2">
          <div
            v-for="item in timeline"
            :key="`${item.event}-${item.at}`"
            class="flex gap-2 text-xs text-n-slate-11"
          >
            <span
              class="mt-1 size-1.5 flex-shrink-0 rounded-full bg-n-teal-9"
            />
            <div class="min-w-0">
              <div class="truncate font-medium text-n-slate-12">
                {{ item.event }}
              </div>
              <div class="truncate">
                {{ item.detail || formatTimelineDate(item.at) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Button
        class="mt-4 w-full"
        label="Salvar análise"
        icon="i-lucide-save"
        teal
        sm
        :is-loading="isSaving"
        @click="saveSupervision"
      />
    </section>
  </div>
</template>
